// Sticky WhatsApp + Email contact buttons for all public pages (landing, /about, /international).
// Floats bottom-right. Hidden inside app (logged-in POS UI).
import React, { useState } from "react";
import { tr } from '@/i18n/tr';
import { MessageCircle, Mail, X, Phone } from "lucide-react";

// Sellox business contacts — edit here in one place.
export const SELLOX_CONTACT = {
  whatsapp: "584248476761",          // +58 424 847 6761
  whatsapp_display: "+58 424 847 6761",
  email: "hello@sellox.shop",
  prefill_en: "Hi Sellox! I'm interested in your POS for my shop.",
  prefill_es: "¡Hola Sellox! Estoy interesado en su POS para mi negocio.",
  prefill_pt: "Olá Sellox! Tenho interesse no seu PDV para minha loja.",
  prefill_zh: tr('x_4f100f54'),
};

export default function FloatingContact({ lang = "en" }) {
  const [open, setOpen] = useState(false);
  const prefill = SELLOX_CONTACT[`prefill_${lang}`] || SELLOX_CONTACT.prefill_en;
  const waLink = `https://wa.me/${SELLOX_CONTACT.whatsapp}?text=${encodeURIComponent(prefill)}`;
  const mailLink = `mailto:${SELLOX_CONTACT.email}?subject=${encodeURIComponent("Sellox inquiry")}&body=${encodeURIComponent(prefill)}`;

  const labels = {
    en: { title: "Contact us", wa: "WhatsApp", mail: "Email", close: "Close" },
    es: { title: "Contáctanos", wa: "WhatsApp", mail: "Email", close: "Cerrar" },
    pt: { title: "Fale conosco", wa: "WhatsApp", mail: "Email", close: "Fechar" },
    zh: { title: tr('x_0cbf83cc'), wa: "WhatsApp", mail: tr('x_e9e8054f'), close: tr('x_b15d9127') },
  }[lang] || { title: "Contact", wa: "WhatsApp", mail: "Email", close: "Close" };

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3">
      {open && (
        <div className="flex flex-col gap-2 bg-slate-900/95 backdrop-blur border border-slate-700 rounded-2xl p-3 shadow-2xl animate-in fade-in slide-in-from-bottom-2 min-w-[220px]" data-testid="contact-panel">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">{labels.title}</span>
            <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white" aria-label={labels.close}>
              <X className="w-4 h-4" />
            </button>
          </div>
          <a href={waLink} target="_blank" rel="noreferrer"
             className="flex items-center gap-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 hover:bg-emerald-500/20 transition"
             data-testid="contact-whatsapp">
            <MessageCircle className="w-5 h-5 text-emerald-400" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-white">{labels.wa}</span>
              <span className="text-[11px] text-slate-400">{SELLOX_CONTACT.whatsapp_display}</span>
            </div>
          </a>
          <a href={mailLink}
             className="flex items-center gap-3 px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 transition"
             data-testid="contact-email">
            <Mail className="w-5 h-5 text-blue-400" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-white">{labels.mail}</span>
              <span className="text-[11px] text-slate-400">{SELLOX_CONTACT.email}</span>
            </div>
          </a>
        </div>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className={`group w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all ${open ? "bg-slate-700 hover:bg-slate-600" : "bg-emerald-500 hover:bg-emerald-400 hover:scale-110 shadow-emerald-500/40"}`}
        aria-label={labels.title}
        data-testid="floating-contact-fab"
      >
        {open
          ? <X className="w-6 h-6 text-white" />
          : <Phone className="w-6 h-6 text-slate-900" />}
      </button>
    </div>
  );
}
